import { Debounce } from "../utilities/Debounce.js";
import { Behaviour } from "../utilities/ResponsiveBehaviour.js";

/**
 * Desktop navigation controller
 * --------------------------------
 * - Level-1 items:
 *    * Hover (debounced): opens either a level-2 menu (SVG toggle) or a dialog (button)
 *    * Hover on an item without children: closes all menus/dialogs
 * - Level-2 items:
 *    * Hover: opens level-3 if present; hovering a plain link closes level-3 for that level-2
 *    * On leaving a level-2 item, we restore level-3 to the "current" level-2
 *      only if the pointer moves outside the submenu container
 * - Toggles (SVG / button):
 *    * Click toggles the controlled element and syncs aria-expanded
 *    * ESC closes the controlled element and focuses the toggle
 * - Dialogs:
 *    * When a dialog opens, close all menus and other dialogs,
 *      and focus the first input/textarea/select if available
 *    * While an input inside an open dialog is focused, "nav mouseleave" won’t close anything
 */
export class MenuDesktop implements Behaviour {
    private root: HTMLElement | Document;

    // --- Fast lookups --------------------------------------------------------

    /** Toggle element -> controlled element (menu or dialog). */
    private toggleToControlled = new Map<HTMLElement, HTMLElement>();

    /** <li> element -> its toggle + controlled pair (for hover intent). */
    private itemToPair = new Map<HTMLElement, { toggleEl: HTMLElement; controlledEl: HTMLElement }>();

    // --- Hover state (only level-1 uses debounce) ---------------------------

    /** <li> -> function to run on mouseenter (debounced at level-1). */
    private itemEnterActionMap = new Map<HTMLElement, (itemEl: HTMLElement) => void>();

    /** <li> -> canceller for the pending debounced mouseenter (level-1 only). */
    private itemLeaveCancelMap = new Map<HTMLElement, () => void>();

    /** For dialog toggles: when they were last opened via hover (ms timestamp). */
    private dialogHoverOpenAt = new Map<HTMLElement, number>();

    // --- Bound handlers ------------------------------------------------------

    private handleDocumentClickBound = (e: MouseEvent) => this.handleDocumentClick(e);
    private handleKeydownBound = (e: KeyboardEvent) => this.handleKeydown(e);
    private handleToggleClickBound = (e: Event) => this.handleToggleClick(e);
    private handleItemEnterBound = (e: Event) => this.handleItemEnter(e);
    private handleItemLeaveBound = (e: MouseEvent) => this.handleItemLeave(e);
    private handleLevel2ClickBound = (e: Event) => this.handleLevel2Click(e);
    private handleNavEnterBound = (_e: MouseEvent) => this.handleNavEnter();
    private handleNavLeaveBound = (_e: MouseEvent) => this.handleNavLeave();

    // --- Timings -------------------------------------------------------------

    private static readonly HOVER_DELAY_MS = 120;     // Debounce for level-1 hover
    private static readonly HOVER_DIALOG_CLICK_SUPPRESS_MS = 1000; // Supress change of event for search button hover
    private static readonly NAV_LEAVE_DELAY_MS = 120; // Close delay after leaving the entire nav

    /**
     * Debounced "nav mouseleave": closes everything unless a dialog input is focused.
     */
    private navLeaveDebounced = Debounce.cancellable<() => void>(
        () => {
            if (this.isAnyDialogInputFocused()) return;
            this.closeAll();
            this.hideSubmenuContainerIfNoL2Open();
        },
        MenuDesktop.NAV_LEAVE_DELAY_MS
    );

    constructor(root: HTMLElement | Document = document) {
        this.root = root;
    }

    // =========================================================================
    // Public API
    // =========================================================================

    /** Open a controlled element (menu or dialog) by its id. */
    public openById(id: string) {
        const controlledEl = this.getById(id);
        if (!controlledEl) return;
        const toggleEl = this.getToggleForControlledEl(controlledEl);
        if (toggleEl) this.openControlled(toggleEl, controlledEl);
    }

    /** Close every controlled element and tidy up container visibility. */
    public closeAll() {
        for (const [toggleEl, controlledEl] of this.toggleToControlled.entries()) {
            this.closeControlled(toggleEl, controlledEl);
        }
        this.hideSubmenuContainerIfNoL2Open();
    }

    /** Remove all listeners and clear state. */
    public destroy() {
        document.removeEventListener('click', this.handleDocumentClickBound, true);

        for (const toggleEl of this.toggleToControlled.keys()) {
            toggleEl.removeEventListener('click', this.handleToggleClickBound);
            toggleEl.removeEventListener('keydown', this.handleKeydownBound as any);
        }

        for (const liEl of this.itemToPair.keys()) {
            liEl.removeEventListener('mouseenter', this.handleItemEnterBound);
            liEl.removeEventListener('mouseleave', this.handleItemLeaveBound as unknown as (e: Event) => void);
        }

        // Safety: remove from any <li> we might have wired
        const scope: ParentNode = (this.root as HTMLElement) || document;
        for (const liEl of Array.from(scope.querySelectorAll<HTMLElement>('li[role="menuitem"]'))) {
            liEl.removeEventListener('mouseenter', this.handleItemEnterBound);
            liEl.removeEventListener('mouseleave', this.handleItemLeaveBound as unknown as (e: Event) => void);
        }

        // Nav listeners
        const navEl = this.getNavEl();
        if (navEl) {
            navEl.removeEventListener('mouseenter', this.handleNavEnterBound);
            navEl.removeEventListener('mouseleave', this.handleNavLeaveBound);
        }

        // Cancel debounces
        for (const cancel of this.itemLeaveCancelMap.values()) cancel();
        this.itemLeaveCancelMap.clear();
        this.itemEnterActionMap.clear();

        this.toggleToControlled.clear();
        this.itemToPair.clear();

        // Level-2 delegation
        const level2Menus = Array.from(scope.querySelectorAll<HTMLElement>('[role="menu"][level="2"]'));
        for (const m of level2Menus) m.removeEventListener('click', this.handleLevel2ClickBound, true);

        this.navLeaveDebounced.cancel();
        this.dialogHoverOpenAt.clear();
    }

    // =========================================================================
    // Setup
    // =========================================================================

    /** Attach listeners and index the DOM. */
    public apply() {
        const scope: ParentNode = (this.root as HTMLElement) || document;

        // 1) Collect all toggles:
        //    - Menus:   "][ia-controls]
        //    - Dialogs: li > button[aria-haspopup="dialog"][aria-controls]
        const menuToggles = Array.from(
            scope.querySelectorAll<HTMLElement>('button[aria-controls]')
        );
        const dialogButtons = Array.from(
            scope.querySelectorAll<HTMLButtonElement>(
                'li[role="menuitem"] > button[aria-controls][aria-haspopup="dialog"]'
            )
        );
        const toggles = [...menuToggles, ...dialogButtons];

        toggles.forEach((toggleEl) => {
            const targetId = toggleEl.getAttribute('aria-controls');
            if (!targetId) return;

            const controlledEl = scope.querySelector<HTMLElement>(`#${CSS.escape(targetId)}`);
            if (!controlledEl) return;

            const role = controlledEl.getAttribute('role');
            if (role !== 'menu' && role !== 'dialog') return;

            this.toggleToControlled.set(toggleEl, controlledEl);

            // Sync aria-expanded with visibility and ensure hidden attribute is correct
            const isHidden = this.isHidden(controlledEl);
            toggleEl.setAttribute('aria-expanded', String(!isHidden));
            if (isHidden) controlledEl.hidden = true;

            // Interactions
            toggleEl.addEventListener('click', this.handleToggleClickBound);
            toggleEl.addEventListener('keydown', this.handleKeydownBound as any);

            // Pair <li> with its toggle & controlled element (used for hover intent)
            const liEl = toggleEl.closest<HTMLElement>('li[role="menuitem"]');
            if (liEl) this.itemToPair.set(liEl, { toggleEl, controlledEl });
        });

        // 2) Hover wiring:
        //    - Level 1 items: debounced hover open/close
        //    - Level 2/3 items: immediate
        for (const liEl of Array.from(scope.querySelectorAll<HTMLElement>('li[role="menuitem"]'))) {
            const parentMenuEl = this.closestElement(liEl, '[role="menu"][level]') as HTMLElement | null;
            const level = parentMenuEl ? this.parseLevelNumber(parentMenuEl) : 1;

            let enterAction: (itemEl: HTMLElement) => void;
            let leaveCancel: () => void = () => {};

            if (level === 1) {
                const debounced = Debounce.cancellable<(itemEl: HTMLElement) => void>(
                    (node) => this.handleItemHoverIntent(node),
                    MenuDesktop.HOVER_DELAY_MS
                );
                enterAction = debounced;
                leaveCancel = () => debounced.cancel();
            } else {
                enterAction = (node) => this.handleItemHoverIntent(node);
            }

            this.itemEnterActionMap.set(liEl, enterAction);
            this.itemLeaveCancelMap.set(liEl, leaveCancel);

            liEl.addEventListener('mouseenter', this.handleItemEnterBound);
            liEl.addEventListener('mouseleave', this.handleItemLeaveBound as unknown as (e: Event) => void);
        }

        // 3) Global click-outside
        document.addEventListener('click', this.handleDocumentClickBound, true);

        // 4) Inside level-2 menus, clicking a plain link should close level-3 for that group
        for (const m of Array.from(scope.querySelectorAll<HTMLElement>('[role="menu"][level="2"]'))) {
            m.addEventListener('click', this.handleLevel2ClickBound, true);
        }

        // 5) Close shortly after leaving the entire nav
        const navEl = this.getNavEl();
        if (navEl) {
            navEl.addEventListener('mouseenter', this.handleNavEnterBound);
            navEl.addEventListener('mouseleave', this.handleNavLeaveBound);
        }
    }

    // =========================================================================
    // Event handlers
    // =========================================================================

    /** Click on an SVG/button toggle. */
    private handleToggleClick(e: Event) {
        const toggleEl = e.currentTarget as HTMLElement;
        const controlledEl = this.toggleToControlled.get(toggleEl);
        if (!controlledEl) return;

        const role = controlledEl.getAttribute('role');

        // Only special-case dialogs (like your search popup)
        if (role === 'dialog') {
            const openedAt = this.dialogHoverOpenAt.get(toggleEl);
            if (openedAt != null) {
                const elapsed = performance.now() - openedAt;
                if (elapsed < MenuDesktop.HOVER_DIALOG_CLICK_SUPPRESS_MS) {
                    // ❌ Ignore this click - dialog was just opened via hover
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                } else {
                    // 3s have passed; no longer suppress clicks
                    this.dialogHoverOpenAt.delete(toggleEl);
                }
            }
        }

        // Normal toggle behaviour
        e.preventDefault();
        e.stopPropagation();
        this.toggleControlled(toggleEl, controlledEl);
    }

    /** Key handling for toggles (SVG + real button). */
    private handleKeydown(e: KeyboardEvent) {
        const toggleEl = e.currentTarget as HTMLElement;
        const controlledEl = this.toggleToControlled.get(toggleEl);
        if (!controlledEl) return;

        const isButton = toggleEl.tagName === 'BUTTON';

        if (!isButton && (e.key === 'Enter' || e.key === ' ')) {
            // SVG toggles need explicit activate on Enter/Space
            e.preventDefault();
            this.toggleControlled(toggleEl, controlledEl);
        } else if (e.key === 'Escape') {
            // ESC closes and returns focus to the toggle
            e.preventDefault();
            this.closeControlled(toggleEl, controlledEl);
            toggleEl.focus();
        }
    }

    /** Mouse enters any <li>. */
    private handleItemEnter(e: Event) {
        const liEl = e.currentTarget as HTMLElement;
        const run = this.itemEnterActionMap.get(liEl);
        if (run) run(liEl);
    }

    /**
     * Mouse leaves a level-2 <li>.
     * If the pointer is outside the submenu container, restore level-3 to the "current" level-2.
     */
    private handleItemLeave(e: MouseEvent) {
        const liEl = e.currentTarget as HTMLElement;
        const cancel = this.itemLeaveCancelMap.get(liEl);
        if (cancel) cancel();

        const parentMenuEl = this.closestElement(liEl, '[role="menu"][level]') as HTMLElement | null;
        if (!parentMenuEl) return;

        const level = this.parseLevelNumber(parentMenuEl);
        if (level !== 2) return;

        const containerEl = this.getSubmenuContainerEl();
        const nextTarget = e.relatedTarget as Node | null;
        const movedIntoContainer = !!(containerEl && nextTarget && containerEl.contains(nextTarget));

        if (!movedIntoContainer) this.showLevel3ForCurrentLevel2(parentMenuEl);
    }

    /** Clicking in a level-2 menu on a plain link closes level-3 for that level-2. */
    private handleLevel2Click(e: Event) {
        const target = e.target as HTMLElement;
        const level2MenuEl = this.closestElement(target, '[role="menu"][level="2"]') as HTMLElement | null;
        if (!level2MenuEl) return;

        const toggleAncestor = this.closestElement(target, 'button[aria-controls]');
        if (toggleAncestor) return; // A toggle will manage its submenu itself

        this.closeLevel3ForLevel2(level2MenuEl);
    }

    /** Click outside the nav closes everything. */
    private handleDocumentClick(e: MouseEvent) {
        const clickTarget = e.target as Node;
        const navEl = this.getNavEl() || document.body;

        if (!navEl.contains(clickTarget)) {
            this.closeAll();
            this.hideSubmenuContainerIfNoL2Open();
        }
    }

    /** Cancel pending "nav leave" close if the pointer re-enters. */
    private handleNavEnter() {
        this.navLeaveDebounced.cancel();
    }

    /** Debounced close after leaving the entire nav. */
    private handleNavLeave() {
        this.navLeaveDebounced();
    }

    // =========================================================================
    // Hover intent logic
    // =========================================================================

    /**
     * Decide what to do when hovering a <li>.
     * - Level 1:
     *   * With children (toggle or dialog button): open controlled
     *   * Without children: close everything
     * - Level 2:
     *   * Without children: close only its level-3 group
     */
    private handleItemHoverIntent(liEl: HTMLElement) {
        const pair = this.itemToPair.get(liEl);
        const parentMenuEl = this.closestElement(liEl, '[role="menu"][level]') as HTMLElement | null;
        const level = parentMenuEl ? this.parseLevelNumber(parentMenuEl) : 1;

        if (pair) {
            // Has children: open its controlled (menu or dialog)
            const { toggleEl, controlledEl } = pair;
            if (toggleEl.getAttribute('aria-expanded') !== 'true') {
                // ✅ Use the special "hover" variant
                this.openControlledFromHover(toggleEl, controlledEl);
            }
            return;
        }

        if (level === 1) {
            // Level-1 item without children: close everything
            this.closeAll();
            return;
        }

        if (level === 2 && parentMenuEl) {
            // Level-2 item without children: close its level-3 group
            this.closeLevel3ForLevel2(parentMenuEl);
        }
    }

    // =========================================================================
    // Open / Close mechanics
    // =========================================================================

    private openControlledFromHover(toggleEl: HTMLElement, controlledEl: HTMLElement) {
        if (controlledEl.getAttribute('role') === 'dialog') {
            // Remember when this dialog toggle was hover-opened
            this.dialogHoverOpenAt.set(toggleEl, performance.now());
        }

        this.openControlled(toggleEl, controlledEl);
    }

    /** Toggle the controlled element. */
    private toggleControlled(toggleEl: HTMLElement, controlledEl: HTMLElement) {
        const expanded = toggleEl.getAttribute('aria-expanded') === 'true';
        expanded ? this.closeControlled(toggleEl, controlledEl) : this.openControlled(toggleEl, controlledEl);
    }

    /** Open a controlled element (menu or dialog). */
    private openControlled(toggleEl: HTMLElement, controlledEl: HTMLElement) {
        const role = controlledEl.getAttribute('role');

        // For menus: close siblings at the same level
        if (role === 'menu') {
            const levelStr = controlledEl.getAttribute('level') || '';
            if (levelStr) this.closeOtherMenusAtLevel(controlledEl, levelStr);
        }

        // Show it and mark expanded
        controlledEl.hidden = false;
        toggleEl.setAttribute('aria-expanded', 'true');

        if (role === 'menu') {
            // Opening a level-2 menu
            const level = this.parseLevelNumber(controlledEl);
            if (level === 2) {
                // Ensure submenu container is visible
                this.showSubmenuContainer(controlledEl);

                // Close all other dialogs except the submenu container itself
                const submenuContainerEl = this.getSubmenuContainerEl();
                this.closeDialogsExcept(submenuContainerEl || null);

                // Default level-3 selection for this level-2
                // Note: Disabled for now, because Daniel asked for it
                // this.ensureDefaultLevel3(controlledEl);
            }
        } else if (role === 'dialog') {
            // Opening a top-level dialog (e.g., search)
            // 1) Close all menus and hide submenu container
            this.closeLevel(2); // closes descendants too
            this.hideSubmenuContainerIfNoL2Open();

            // 2) Close other dialogs (keep this one)
            this.closeDialogsExcept(controlledEl);

            // 3) Focus the first input-like control inside
            this.focusFirstFieldInDialog(controlledEl);
        }
    }

    /** Close a controlled element (menu or dialog). */
    private closeControlled(toggleEl: HTMLElement, controlledEl: HTMLElement) {
        controlledEl.hidden = true;
        toggleEl.setAttribute('aria-expanded', 'false');

        const role = controlledEl.getAttribute('role');

        if (role === 'dialog') {
            // Once closed, we don't care about old hover-open timestamps
            this.dialogHoverOpenAt.delete(toggleEl);
        }

        if (role === 'menu') {
            // Compute once and reuse
            const levelNum = this.parseLevelNumber(controlledEl);

            // Close any descendant menus at deeper levels
            if (!Number.isNaN(levelNum)) {
                const deeperMenus = Array.from(
                    ((this.root as ParentNode) || document).querySelectorAll<HTMLElement>('[role="menu"][level]')
                ).filter((m) => this.parseLevelNumber(m) > levelNum);

                for (const sub of deeperMenus) {
                    const subToggle = this.getToggleForControlledEl(sub);
                    if (subToggle) {
                        sub.hidden = true;
                        subToggle.setAttribute('aria-expanded', 'false');
                    } else {
                        sub.hidden = true;
                    }
                }
            }

            // If we just closed a level-2 menu, hide the container if none left open
            if (levelNum === 2) {
                this.hideSubmenuContainerIfNoL2Open(controlledEl.closest('nav') as HTMLElement | null);
            }
        }
    }

    /** Close all menus at a given numeric level. */
    private closeLevel(levelNum: number) {
        const scope = ((this.root as ParentNode) || document);
        const menus = Array.from(scope.querySelectorAll<HTMLElement>(`[role="menu"][level="${levelNum}"]`));
        for (const m of menus) {
            const t = this.getToggleForControlledEl(m);
            if (t) this.closeControlled(t, m);
        }
    }

    /** Close sibling menus at the same level as the given menu. */
    private closeOtherMenusAtLevel(currentMenuEl: HTMLElement, level: string) {
        const allAtLevel = Array.from(
            ((this.root as ParentNode) || document).querySelectorAll<HTMLElement>(
                `[role="menu"][level="${CSS.escape(level)}"]`
            )
        );
        for (const menuEl of allAtLevel) {
            if (menuEl === currentMenuEl) continue;
            const t = this.getToggleForControlledEl(menuEl);
            if (t) this.closeControlled(t, menuEl);
        }
    }

    /** Close all dialogs except the provided one (or close all, if null). */
    private closeDialogsExcept(exceptEl: HTMLElement | null) {
        const navEl = this.getNavEl();
        if (!navEl) return;

        const dialogs = Array.from(navEl.querySelectorAll<HTMLElement>('[role="dialog"]'));
        for (const dialogEl of dialogs) {
            if (exceptEl && dialogEl === exceptEl) continue;
            if (this.isHidden(dialogEl)) continue;

            const t = this.getToggleForControlledEl(dialogEl);
            if (t) this.closeControlled(t, dialogEl);
            else dialogEl.hidden = true;
        }
    }

    // =========================================================================
    // Level-2 → Level-3 default selection
    // =========================================================================

    /**
     * When a level-2 menu opens:
     * 1) If a level-2 item is aria-current:
     *    - If it has children → open its level-3
     *    - Else → close all level-3 for this level-2
     * 2) Else, if any level-3 item is aria-current:
     *    - Open the level-3 that contains that item
     * 3) Else:
     *    - Open the level-3 of the first level-2 item if it has children; otherwise none
     * If a level-3 is already open, user choice wins and we do nothing.
     */
    private ensureDefaultLevel3(level2MenuEl: HTMLElement) {
        const l3Menus = this.getLevel3MenusForLevel2(level2MenuEl);
        const alreadyOpen = l3Menus.some((m) => !this.isHidden(m));
        if (alreadyOpen) return;

        // 1) Current on L2?
        const currentL2Item = this.findCurrentLevel2Item(level2MenuEl);
        if (currentL2Item) {
            const currentToggle = currentL2Item.querySelector<HTMLElement>('button[aria-controls]');
            if (currentToggle) {
                const l3 = this.getLevel3MenuForToggle(currentToggle);
                if (l3) {
                    this.openControlled(currentToggle, l3);
                    return;
                }
            }
            this.closeLevel3ForLevel2(level2MenuEl); // no children
            return;
        }

        // 2) Current inside L3?
        const toggleForL3WithCurrent = this.findToggleWithCurrentInsideLevel3(level2MenuEl);
        if (toggleForL3WithCurrent) {
            const l3 = this.getLevel3MenuForToggle(toggleForL3WithCurrent);
            if (l3) {
                this.openControlled(toggleForL3WithCurrent, l3);
                return;
            }
        }

        // 3) Fallback: first L2 item (if it has children)
        const firstL2Item = level2MenuEl.querySelector<HTMLElement>('li[role="menuitem"]');
        if (!firstL2Item) {
            this.closeLevel3ForLevel2(level2MenuEl);
            return;
        }

        const firstToggle = firstL2Item.querySelector<HTMLElement>('button[aria-controls]');
        if (!firstToggle) {
            this.closeLevel3ForLevel2(level2MenuEl);
            return;
        }

        const l3 = this.getLevel3MenuForToggle(firstToggle);
        if (l3) this.openControlled(firstToggle, l3);
        else this.closeLevel3ForLevel2(level2MenuEl);
    }

    /** After leaving a level-2 item, restore level-3 to the "current" level-2. */
    private showLevel3ForCurrentLevel2(level2MenuEl: HTMLElement) {
        const currentL2Item = this.findCurrentLevel2Item(level2MenuEl);
        if (!currentL2Item) return;

        const currentToggle = currentL2Item.querySelector<HTMLElement>('button[aria-controls]');
        if (currentToggle) {
            const l3 = this.getLevel3MenuForToggle(currentToggle);
            if (l3) {
                this.openControlled(currentToggle, l3);
                return;
            }
        }
        this.closeLevel3ForLevel2(level2MenuEl); // current has no children
    }

    /** Find the level-2 <li> that is aria-current (or contains an aria-current link). */
    private findCurrentLevel2Item(level2MenuEl: HTMLElement): HTMLElement | null {
        const candidate = level2MenuEl.querySelector<HTMLElement>(
            'li[role="menuitem"][aria-current="true"], li[role="menuitem"] a[aria-current="true"]'
        );
        return candidate?.closest('li[role="menuitem"]') || null;
    }

    /** Find a toggle whose level-3 contains an aria-current item (for a given level-2 menu). */
    private findToggleWithCurrentInsideLevel3(level2MenuEl: HTMLElement): HTMLElement | null {
        const togglesInL2 = Array.from(level2MenuEl.querySelectorAll<HTMLElement>('button[aria-controls]'));
        for (const t of togglesInL2) {
            const l3 = this.getLevel3MenuForToggle(t);
            if (!l3) continue;
            const currentInL3 = l3.querySelector<HTMLElement>('li[role="menuitem"][aria-current="true"], a[aria-current="true"]');
            if (currentInL3) return t;
        }
        return null;
    }

    /** Close only the level-3 menus that belong to this specific level-2 menu. */
    private closeLevel3ForLevel2(level2MenuEl: HTMLElement) {
        const l3Menus = this.getLevel3MenusForLevel2(level2MenuEl);
        for (const m of l3Menus) {
            const t = this.getToggleForControlledEl(m);
            if (t) this.closeControlled(t, m);
        }
    }

    /** All level-3 menus that belong to a given level-2 menu. */
    private getLevel3MenusForLevel2(level2MenuEl: HTMLElement): HTMLElement[] {
        const togglesInL2 = Array.from(level2MenuEl.querySelectorAll<HTMLElement>('button[aria-controls]'));
        const menus: HTMLElement[] = [];
        for (const t of togglesInL2) {
            const m = this.getLevel3MenuForToggle(t);
            if (m) menus.push(m);
        }
        return menus;
    }

    /** Given a toggle inside a level-2 menu, return its controlled level-3 menu (if any). */
    private getLevel3MenuForToggle(toggleEl: HTMLElement): HTMLElement | null {
        const controlledEl = this.toggleToControlled.get(toggleEl)
            || this.getById(toggleEl.getAttribute('aria-controls') || '');
        if (controlledEl && controlledEl.matches?.('[role="menu"][level="3"]')) {
            return controlledEl;
        }
        return null;
    }

    // =========================================================================
    // Submenu container helpers (current DOM layout)
    // =========================================================================

    /**
     * Locate the submenu container:
     * - Prefer `nav > div > div` that actually contains `[role="menu"][level="2"]`
     * - Fallback to `nav > div` if it directly contains level-2 menus
     */
    private getSubmenuContainerEl(): HTMLElement | null {
        const navEl = this.getNavEl();
        if (!navEl) return null;

        const wrapperEl = navEl.querySelector<HTMLElement>(':scope > div');
        if (!wrapperEl) return null;

        const innerDivs = Array.from(wrapperEl.querySelectorAll<HTMLElement>(':scope > div'));
        const innerContainer = innerDivs.find(d => d.querySelector('[role="menu"][level="2"]'));
        if (innerContainer) return innerContainer;

        if (wrapperEl.querySelector('[role="menu"][level="2"]')) return wrapperEl;

        return null;
    }

    /** Ensure the submenu container is visible. */
    private showSubmenuContainer(_contextMenuEl: HTMLElement) {
        const containerEl = this.getSubmenuContainerEl();
        if (containerEl) containerEl.hidden = false;
    }

    /** Hide the submenu container if no level-2 menus are open. */
    private hideSubmenuContainerIfNoL2Open(navEl?: HTMLElement | null) {
        navEl = navEl || this.getNavEl();
        if (!navEl) return;

        const anyL2Open = Array.from(navEl.querySelectorAll<HTMLElement>('[role="menu"][level="2"]'))
            .some((m) => !this.isHidden(m));

        if (!anyL2Open) {
            const containerEl = this.getSubmenuContainerEl();
            if (containerEl) containerEl.hidden = true;
        }
    }

    /** The <nav> element this instance controls. */
    private getNavEl(): HTMLElement | null {
        const explicit = (this.root as HTMLElement)?.nodeType === Node.ELEMENT_NODE
            ? ((this.root as HTMLElement).closest('nav') || (this.root as HTMLElement).querySelector('nav'))
            : null;
        return explicit || document.querySelector('nav');
    }

    // =========================================================================
    // Focus helpers (dialogs)
    // =========================================================================

    /** Focus the first input/textarea/select inside a dialog, if any. */
    private focusFirstFieldInDialog(dialogEl: HTMLElement) {
        // Use a microtask to let the dialog render before focusing
        setTimeout(() => {
            const firstField = dialogEl.querySelector<HTMLElement>('input, textarea, select');
            if (firstField) {
                firstField.focus({ preventScroll: true });
                try { (firstField as HTMLInputElement).select?.(); } catch { /* ignore */ }
            }
        }, 0);
    }

    /** True if the activeElement is an input/textarea/select inside an open dialog. */
    private isAnyDialogInputFocused(): boolean {
        const active = document.activeElement as HTMLElement | null;
        if (!active) return false;

        const isInputLike =
            active.matches?.('input, textarea, select') ||
            !!active.closest?.('input, textarea, select');

        if (!isInputLike) return false;

        const navEl = this.getNavEl();
        if (!navEl) return false;

        const openDialogs = Array.from(navEl.querySelectorAll<HTMLElement>('[role="dialog"]:not([hidden])'));
        return openDialogs.some(d => d.contains(active));
        }

    // =========================================================================
    // Generic utilities
    // =========================================================================

    /** Find the toggle for a given controlled element (menu or dialog). */
    private getToggleForControlledEl(controlledEl: HTMLElement): HTMLElement | null {
        for (const [toggleEl, c] of this.toggleToControlled.entries()) {
            if (c === controlledEl) return toggleEl;
        }
        const id = controlledEl.id;
        if (!id) return null;
        return ((this.root as ParentNode) || document).querySelector<HTMLElement>(
            `[aria-controls="${CSS.escape(id)}"]`
        );
    }

    /** Whether an element is hidden (via [hidden] or CSS visibility/display). */
    private isHidden(el: HTMLElement): boolean {
        if (el.hidden) return true;
        const style = window.getComputedStyle(el);
        return style.display === 'none' || style.visibility === 'hidden';
    }

    /** Parse the numeric "level" attribute from a [role="menu"] element. */
    private parseLevelNumber(menuEl: HTMLElement): number {
        return parseInt(menuEl.getAttribute('level') || '0', 10);
    }

    /** Safe getElementById scoped to this.root when possible. */
    private getById(id: string): HTMLElement | null {
        if (!id) return null;
        const scope = (this.root as Document | HTMLElement) as any;
        return scope.getElementById ? scope.getElementById(id) : document.getElementById(id);
    }

    /** Lightweight closest() helper (safe for older DOMs if needed). */
    private closestElement(el: Element | null, selector: string): Element | null {
        while (el && el.nodeType === 1) {
            if ((el as Element).matches(selector)) return el;
            el = el.parentElement!;
        }
        return null;
    }
}
